/**
 * Indicatore freshness MFA in header (vedi design.md §12.3).
 *
 * - Tier 2 (auth_time <= 5min):  verde, "🔒 Verificato — adesso"
 * - Tier 1 (5min - 12h):          grigio, "🔒 Verificato — Xh fa"
 * - Tier 0 (>12h o assente):     rosso, "🔓 Verifica scaduta — rinnova"
 *
 * Click sulla pill (tier1/tier0) apre uno step-up MFA volontario.
 * Nascosta per PAZIENTE.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useMfaStepUp } from '../../hooks/useMfaStepUp';
import { isStaffRole } from '@shared/types/auth';

// Esportato per test puri (formatRelative.test.ts). Convive col componente
// React nello stesso file per leggibilità.
// eslint-disable-next-line react-refresh/only-export-components
export function formatRelative(
  authTimeSec: number | null,
  nowMs: number = Date.now()
): string {
  if (!authTimeSec) return '—';
  const nowSec = Math.floor(nowMs / 1000);
  const ageSec = Math.max(0, nowSec - authTimeSec);
  if (ageSec < 60) return 'adesso';
  const ageMin = Math.floor(ageSec / 60);
  if (ageMin < 60) return `${ageMin}min fa`;
  const ageH = Math.floor(ageMin / 60);
  return `${ageH}h fa`;
}

export interface MfaFreshnessPillProps {
  /** Test seam */
  nowProvider?: () => number;
}

export function MfaFreshnessPill({ nowProvider }: MfaFreshnessPillProps = {}) {
  const { user, role, mfaTier, authTimeSec } = useAuth();
  const { requireTier1 } = useMfaStepUp();
  const [, forceTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => forceTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!user || !isStaffRole(role)) return null;

  const label = formatRelative(authTimeSec, nowProvider?.());

  if (mfaTier === 2) {
    return (
      <span
        role="status"
        aria-label="Identità verificata di recente"
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success/15 text-success font-body-sm font-semibold"
      >
        <span className="material-symbols-outlined !text-[18px]" aria-hidden>
          lock
        </span>
        Verificato — {label}
      </span>
    );
  }

  if (mfaTier === 1) {
    return (
      <button
        type="button"
        onClick={() =>
          requireTier1('Rinnova verifica identità').catch(() => {})
        }
        aria-label="Rinnova verifica identità"
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface-container-low text-text-secondary font-body-sm font-semibold hover:bg-surface-container transition-colors"
      >
        <span className="material-symbols-outlined !text-[18px]" aria-hidden>
          lock
        </span>
        Verificato — {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() =>
        requireTier1('Rinnova verifica identità').catch(() => {})
      }
      aria-label="Verifica MFA scaduta, clic per rinnovare"
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-error-container text-on-error-container font-body-sm font-semibold hover:opacity-90 transition-opacity"
    >
      <span className="material-symbols-outlined !text-[18px]" aria-hidden>
        lock_open
      </span>
      Verifica scaduta — rinnova
    </button>
  );
}
